import { useContext, useState } from "react";
import { AuthContext } from "../auth/AuthContext";
import "../styles/EditProfileForm.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function EditProfileForm({ onProfileUpdated }) {
  const { user, accessToken } = useContext(AuthContext);

  const [username, setUsername] = useState(user.username || "");
  const [bio, setBio] = useState(user.bio || "");

  const [profilePreview, setProfilePreview] = useState(
    user.profile_picture ||
      "https://cdn-icons-png.flaticon.com/512/847/847969.png"
  );
  const [backgroundPreview, setBackgroundPreview] = useState(
    user.background_picture ||
      "https://images.unsplash.com/photo-1517817748490-6c62e0eab2f4?auto=format&fit=crop&w=1200&q=80"
  );

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setter(previewUrl);
    }
  };

  const parseApiError = async (res) => {
    // Spróbujmy wyciągnąć sensowny komunikat z DRF/JSON
    try {
      const data = await res.json();
      if (!data) return "Nie udało się zapisać zmian.";

      if (typeof data === "string") return data;

      // typowe DRF: { field: ["msg1", "msg2"], ... } albo { detail: "..." }
      if (data.detail) return data.detail;

      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const val = data[firstKey];
        if (Array.isArray(val)) return val.join(" ");
        if (typeof val === "string") return val;
        return "Nie udało się zapisać zmian.";
      }

      return "Nie udało się zapisać zmian.";
    } catch {
      return "Nie udało się zapisać zmian.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    const trimmedUsername = username.trim();

    if (!trimmedUsername) {
      setError("Nazwa użytkownika nie może być pusta.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("username", trimmedUsername);
    formData.append("bio", bio);

    const avatarFile = document.getElementById("avatarUpload")?.files?.[0];
    const bgFile = document.getElementById("backgroundUpload")?.files?.[0];
    if (avatarFile) formData.append("profile_picture", avatarFile);
    if (bgFile) formData.append("background_picture", bgFile);

    let res;
    try {
      res = await fetch(`${API_BASE}/api/users/me/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`, // bez Content-Type przy FormData
        },
        body: formData,
      });
    } catch (err) {
      setError("Błąd połączenia z serwerem.");
      setLoading(false);
      return;
    }

    if (!res.ok) {
      const msg = await parseApiError(res);
      setError(msg);
    } else {
      const data = await res.json();
      setSuccess(true);
      onProfileUpdated?.(data);
    }

    setLoading(false);
  };

  return (
  <form onSubmit={handleSubmit} className="epf">
    {/* Username */}
    <div className="mb-3">
      <label className="form-label fw-semibold epf__label">Nazwa użytkownika</label>
      <input
        type="text"
        className="form-control epf__input"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="np. michal_92"
        autoComplete="off"
      />
      <div className="form-text epf__help">
        Zmiana nazwy użytkownika może wpływać na link do profilu i widoczność w aplikacji.
      </div>
    </div>

    {/* Background */}
    <div className="mb-3">
      <label className="form-label fw-semibold epf__label">Tło profilu</label>

      <div className="epf__preview epf__preview--wide mb-2">
        <img src={backgroundPreview} alt="Tło" className="epf__img epf__img--wide" />
      </div>

      <input
        id="backgroundUpload"
        type="file"
        accept="image/*"
        className="form-control form-control-sm epf__file"
        onChange={(e) => handleFileChange(e, setBackgroundPreview)}
      />
    </div>

    {/* Avatar */}
    <div className="mb-3">
      <label className="form-label fw-semibold epf__label">Zdjęcie profilowe</label>

      <div className="epf__preview epf__preview--round mb-2">
        <img src={profilePreview} alt="Avatar" className="epf__img epf__img--round" />
      </div>

      <input
        id="avatarUpload"
        type="file"
        accept="image/*"
        className="form-control form-control-sm epf__file"
        onChange={(e) => handleFileChange(e, setProfilePreview)}
      />
    </div>

    {/* Bio */}
    <div className="mb-3">
      <label className="form-label fw-semibold epf__label">Bio</label>
      <textarea
        className="form-control epf__textarea"
        rows="4"
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        placeholder="Opowiedz coś o sobie..."
      />
    </div>

    {error && <div className="alert alert-danger small">{error}</div>}
    {success && <div className="alert alert-success small">Zapisano zmiany!</div>}

    <div className="text-end">
      <button
        type="submit"
        className="btn epf__btnPrimary rounded-pill px-4"
        disabled={loading}
      >
        {loading ? "Zapisywanie..." : "Zapisz zmiany"}
      </button>
    </div>
  </form>
);
}
